import { type RefObject, useEffect, useRef } from 'react';
import logMapOverlayDisplayed from '@alltrails/amplitude/events/logMapOverlayDisplayed';
import { hasFeatureEntitlement } from '@alltrails/context/hooks/useFeatureEntitlements';
import useUser from '@alltrails/context/hooks/useUser';
import { useClientNavigationPathname } from '@alltrails/core/hooks/useClientNavigation';
import useIsVisible from '@alltrails/core/hooks/useIsVisible';
import type { StyleId } from '../types/Styles';
import { getMapDrawerLocation, getMapOverlay, getPage } from '../utils/amplitudeHelpers';
import { dynamicStyleCardConfigs } from '../utils/styleCardConfigs';
import { isBaseStyle, isDynamicStyle } from '../utils/styleIdHelpers';

type Args = {
  disableAnalytics?: boolean;
  itemRef: RefObject<HTMLElement | null>;
  loggedOverlayIds?: RefObject<Set<StyleId>>;
  styleId: StyleId;
};

const useLogMapOverlayDisplayed = ({ disableAnalytics, itemRef, loggedOverlayIds: loggedOverlayIdsFromMenu, styleId }: Args) => {
  const pathname = useClientNavigationPathname();
  const user = useUser();
  const isVisible = useIsVisible(itemRef);
  const localLoggedOverlayIds = useRef(new Set<StyleId>());
  const loggedOverlayIds = loggedOverlayIdsFromMenu ?? localLoggedOverlayIds;

  useEffect(() => {
    if (disableAnalytics || loggedOverlayIds.current.has(styleId) || !isVisible) {
      return;
    }
    if (isBaseStyle(styleId)) {
      return;
    }

    const mapOverlay = getMapOverlay(styleId);
    if (!mapOverlay) {
      return;
    }

    const featureEntitlement = isDynamicStyle(styleId) ? dynamicStyleCardConfigs[styleId].featureEntitlement : undefined;
    const isLocked = Boolean(featureEntitlement && !hasFeatureEntitlement(user, featureEntitlement));

    loggedOverlayIds.current.add(styleId);
    logMapOverlayDisplayed({
      is_locked: isLocked,
      map_drawer_location: getMapDrawerLocation(styleId),
      map_location: getPage(pathname),
      map_overlay: mapOverlay
    });
  }, [disableAnalytics, isVisible, loggedOverlayIds, pathname, styleId, user]);
};

export default useLogMapOverlayDisplayed;
